import "./custom.css";

import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, MenuItem, Tooltip } from "@mui/material";
import classNames from "classnames";
import { Link, useParams } from "react-router-dom";

import defaultStyleThumbnail from "../../assets/default_style_profile.png";
import {
  User,
  Workspace,
  WorkspaceMembership,
} from "../../components/graphql/schema.graphql";
import { useBasicData } from "../../hooks";
import {
  getConnectionNodes,
  getConnectionNodesTyped,
  RelayConnection,
} from "../../utils/relay";
import { truncate } from "../../utils/strings";
import { Permission } from "../graphql/customTypes";
import { getForgeUrl } from "../inference/utils";
import ForgeIcon from "../ui/icons/ForgeIcon";
import DefaultProfileImage from "./DefaultProfileImage";
import WorkspaceSelectionButton from "./WorkspaceSelectionButton";

type AppMenuWorkspace = Pick<
  Workspace,
  "__typename" | "id" | "name" | "profilePicture" | "myRole" | "myPermissions"
>;

type AppMenuMembership = Pick<WorkspaceMembership, "__typename" | "id"> & {
  workspace: AppMenuWorkspace;
};

type AppMenuUser = Pick<
  User,
  "__typename" | "profilePicture" | "memberships"
> & {
  personalWorkspace?: AppMenuWorkspace;
  memberships: RelayConnection<AppMenuMembership>;
};

type AppMenuItem = {
  id: string;
  name: string;
  route: string[];
  icon: JSX.Element;
  colors: string;
  className?: string;
  disabled: boolean;
  dataCy?: string;
};

function getAppMenuItems(
  workspace: AppMenuWorkspace,
  me: AppMenuUser,
): AppMenuItem[] {
  const items = [];
  if (!workspace || !workspace.myRole) {
    if (me) {
      if (me.personalWorkspace) {
        items.push({
          id: me.personalWorkspace.id,
          name: me.personalWorkspace.name,
          route: [`/${me.personalWorkspace.name}`],
          icon: <img src={me.profilePicture} className="w-[20px] rounded" />,
          colors: "text-layer-600 bg-transparent",
          className: "mb-1",
          disabled: false,
          dataCy: "sidebar-workspace",
        });
      }
      getConnectionNodesTyped(me.memberships)
        ?.filter((w) => w.workspace?.id !== me.personalWorkspace?.id)
        ?.sort((a, b) => a.workspace.name.localeCompare(b.workspace.name))
        ?.forEach((membership) => {
          items.push({
            id: membership.workspace.id,
            name: truncate(membership.workspace.name, 18),
            route: [`/${membership.workspace.name}`],
            icon: membership.workspace.profilePicture ? (
              <img
                src={membership.workspace.profilePicture}
                className="w-[20px] rounded bg-layer-700 before:content-['🏢'] before:w-full before:h-full before:block before:bg-layer-600 before:grayscale before:text-center"
              />
            ) : (
              <DefaultProfileImage
                workspaceName={membership.workspace.name}
                className="text-xs w-[20px] rounded"
              />
            ),
            colors: "text-layer-600 bg-white/75",
            className: "mb-1",
            disabled: false,
            dataCy: "sidebar-workspace",
          });
        });
    }
    return items;
  } else {
    items.push({
      id: "forge",
      name: "Forge",
      route: [`/${workspace.name}`, `/${workspace.name}/forge`],
      icon:
        location.pathname === `/${workspace.name}` ||
        location.pathname.includes(`/${workspace.name}/forge`) ? (
          <ForgeIcon weight="solid" />
        ) : (
          <ForgeIcon weight="regular" />
        ),
      colors: "text-[inherit] bg-transparent",
      className: "mb-1",
      disabled: false,
    });
    if (workspace.myPermissions.includes(Permission.ViewAsset)) {
      items.push({
        id: "drive",
        name: "Library",
        route: [`/${workspace.name}/drive`],
        icon: location.pathname.includes(`/${workspace.name}/drive`) ? (
          <FontAwesomeIcon
            icon={icon({
              name: "layer-group",
              family: "sharp",
              style: "solid",
            })}
          />
        ) : (
          <FontAwesomeIcon
            icon={icon({
              name: "layer-group",
              family: "sharp",
              style: "regular",
            })}
          />
        ),
        colors: "text-[inherit] bg-transparent",
        className: "mb-1",
        disabled: false,
      });
    }
    items.push({
      id: "styles",
      name: "Styles",
      route: [`/${workspace.name}/styles`],
      icon: location.pathname.includes(`/${workspace.name}/styles`) ? (
        <FontAwesomeIcon
          icon={icon({
            name: "palette",
            family: "sharp",
            style: "solid",
          })}
        />
      ) : (
        <FontAwesomeIcon
          icon={icon({
            name: "palette",
            family: "sharp",
            style: "regular",
          })}
        />
      ),
      colors: "text-[inherit] bg-transparent",
      className: "mb-1",
      disabled: false,
    });
    if (
      workspace.myPermissions.includes(Permission.ViewStyleHistory) ||
      workspace.myPermissions.includes(Permission.UseStyle)
    ) {
      items.push({
        id: "prompts",
        name: "History",
        route: [`/${workspace.name}/prompts`],
        icon: location.pathname.includes(`/${workspace.name}/prompts`) ? (
          <FontAwesomeIcon
            icon={icon({
              name: "clock-rotate-left",
              family: "sharp",
              style: "solid",
            })}
          />
        ) : (
          <FontAwesomeIcon
            icon={icon({
              name: "clock-rotate-left",
              family: "sharp",
              style: "regular",
            })}
          />
        ),
        colors: "text-[inherit] bg-transparent",
        className: "mb-4",
        disabled: false,
      });
    }
  }
  return items;
}

type AppNavSidebarProps = {
  fixed: boolean;
  open: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
};

export default function AppNavSidebar({
  fixed,
  open,
  onMouseEnter,
  onMouseLeave,
}: AppNavSidebarProps) {
  const { workspace: workspaceName } = useParams();
  const { user, workspace, workspaceError } = useBasicData();

  const styles = getConnectionNodes(workspace?.styles);
  const isAnyStylePinned = styles?.some((style) => style.isPinned) ?? false;
  const isWorkspaceless = !workspace || !workspace.myRole;
  const isInWorkspace = !isWorkspaceless && !!user;

  if (!!workspaceName && !workspace && !workspaceError) {
    return null;
  }

  const appMenuItems = getAppMenuItems(workspace, user);

  return (
    <>
      <div
        className={classNames({
          "transition-all duration-500": true,
          "w-[230px] shrink-0": fixed,
          "w-0": !fixed,
        })}
      />

      <nav
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className={classNames(
          "top-0 w-[230px] h-full max-h-screen transition-all ease-out duration-300 z-[200]",
          {
            [fixed ? "fixed pl-0 pr-4" : "fixed pl-4 pr-4 py-16"]: true,
            "ml-0": fixed || open,
            "ml-[-230px]": !fixed && !open,
          },
        )}
        data-cy="nav-sidebar"
      >
        <div className="relative h-full">
          <div
            className={classNames(
              "rounded-lg backdrop-blur-md absolute left-0 top-0 transition-all duration-300 ease-out origin-top-left border bg-white border-layer-300 dark:bg-layer-900 dark:border-layer-700/50",
              {
                "h-0 w-0 opacity-0 scale-95 shadow-none": fixed,
                "h-full w-[206px] opacity-100 scale-100 shadow-sidebar dark:shadow-2xl dark:shadow-black/40":
                  !fixed,
              },
            )}
          />

          <div
            className={classNames(
              "transition-all ease-out duration-300 overflow-y-auto hide_scrollbar-track text-layer-700 dark:text-layer-300",
              {
                "h-full rounded-none pl-4 pr-0": fixed,
                "h-[calc(100%_-_2px)] rounded-[0.5rem] px-2 translate-y-px":
                  !fixed,
              },
            )}
          >
            {workspace?.myRole ? (
              <div
                className={classNames({
                  "cursor-default select-none transition-all ease-out duration-300":
                    true,
                  "h-8 mb-5 mt-3 -ml-2": fixed,
                  "-ml-2 z-[1] relative mb-3 p-px h-12 w-[206px] -translate-y-px":
                    !fixed,
                })}
              >
                <div
                  className={classNames(
                    "flex text-layer-50 transition-all ease-out duration-300 border-b border-solid",
                    {
                      "border-white dark:border-layer-950 max-w-[189px]": fixed,
                      "rounded-t-lg p-2 pl-2.5 border-layer-300 dark:border-layer-700/50 max-w-[204px]":
                        !fixed,
                    },
                  )}
                >
                  <WorkspaceSelectionButton fixed={fixed} />
                </div>
              </div>
            ) : null}

            <ul>
              {!isInWorkspace && appMenuItems.length > 0 && (
                <li className="relative px-2 pt-2 pb-3 text-sm font-bold select-none text-layer-500 dark:text-layer-600">
                  Your Workspaces
                </li>
              )}

              {appMenuItems.map((item) => (
                <MenuItem
                  key={item.id}
                  className={classNames(
                    "relative p-0 h-7 rounded-md min-w-[186px] max-w-[188px] group",
                    item.className,
                  )}
                  sx={{
                    backgroundColor: "none !important",
                    "&:hover": {
                      background: "none",
                    },
                  }}
                  data-tour={`sidebar-${item.id}`}
                  data-cy={item.dataCy ?? `sidebar-${item.id}`}
                >
                  {item.disabled && (
                    <Tooltip title="Coming Soon" followCursor={true}>
                      <button className="absolute top-0 left-0 h-full w-full z-[1] cursor-default" />
                    </Tooltip>
                  )}

                  <Link
                    to={item.route[0]}
                    className={classNames(
                      "h-7 flex flex-grow space-x-1 rounded-md ring-1 ring-inset",
                      {
                        "hover:bg-layer-200 dark:hover:bg-layer-800": fixed,
                        "dark:bg-layer-800 dark:ring-layer-700/50":
                          fixed &&
                          (item.route.includes(location.pathname) ||
                            location.pathname.includes(item.id)),
                        "hover:bg-layer-200 dark:hover:bg-layer-700": !fixed,
                        "dark:bg-layer-700 dark:ring-layer-600/75":
                          !fixed &&
                          (item.route.includes(location.pathname) ||
                            location.pathname.includes(item.id)),
                        "font-bold text-layer-800 bg-layer-200 ring-layer-200 dark:text-layer-200":
                          item.route.includes(location.pathname) ||
                          location.pathname.includes(item.id),
                        "font-medium ring-transparent text-layer-800 dark:text-layer-200":
                          !item.route.includes(location.pathname) &&
                          !location.pathname.includes(item.id),
                        "opacity-40 pointer-events-none": item.disabled,
                        "opacity-100": !item.disabled,
                      },
                    )}
                  >
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg m-0 mr-0.5">
                      <div
                        className={classNames(
                          "w-5 h-5 flex items-center justify-center rounded m-0 text-sm text-layer-500 dark:text-layer-300",
                          item.colors,
                        )}
                      >
                        {item.icon}
                      </div>
                    </div>

                    <Box className="flex items-center text-sm font-[inherit] select-none pr-2 normal-case">
                      {item.name}
                    </Box>
                  </Link>
                </MenuItem>
              ))}
            </ul>

            <div className="relative w-full">
              <ul className="w-full py-2">
                {isAnyStylePinned && (
                  <>
                    <li>
                      <h4 className="px-1 pb-2 text-xs font-bold select-none opacity-90 text-layer-500 dark:text-layer-300">
                        Favorites
                      </h4>
                    </li>
                    {styles
                      .filter((s) => s.isPinned === true)
                      .map((style) => (
                        <div key={style.id}>
                          <MenuItem
                            key={style.id}
                            className="relative group p-0 h-7 rounded-md w-full min-w-[186px] max-w-[188px] overflow-hidden mb-1"
                            sx={{
                              backgroundColor: "none !important",
                              "&:hover": {
                                background: "none",
                              },
                            }}
                          >
                            <Link
                              to={getForgeUrl({
                                workspaceName: workspace.name,
                                inputs: {
                                  parameters: {
                                    styles: [{ id: style.id, weight: 1 }],
                                    images: [],
                                  },
                                },
                              })}
                              className={classNames(
                                "h-7 flex items-center flex-grow space-x-1 rounded-md ring-1 ring-inset",
                                {
                                  "hover:bg-layer-200 dark:hover:bg-layer-800":
                                    fixed,
                                  "dark:bg-layer-800 dark:ring-layer-700/50":
                                    fixed && location.href.includes(style.id),
                                  "hover:bg-layer-200 dark:hover:bg-layer-700":
                                    !fixed,
                                  "dark:bg-layer-700 dark:ring-layer-600/75":
                                    !fixed && location.href.includes(style.id),
                                  "font-medium text-layer-800 bg-layer-200 ring-layer-200 dark:text-layer-200":
                                    location.href.includes(style.id),
                                  "font-normal ring-transparent text-layer-800 dark:text-layer-200":
                                    !location.href.includes(style.id),
                                },
                              )}
                            >
                              <div className="w-7 h-7 flex items-center justify-center rounded-lg m-0 mr-0.5 shrink-0">
                                <div
                                  className={classNames(
                                    "relative w-5 h-5 border rounded-full overflow-clip",
                                    {
                                      "border-layer-300 dark:border-layer-700":
                                        fixed,
                                      "border-layer-300 dark:border-layer-600":
                                        !fixed,
                                    },
                                  )}
                                >
                                  <img
                                    src={
                                      style?.featuredFiles?.[0]?.file?.url ??
                                      defaultStyleThumbnail
                                    }
                                    width="100%"
                                    className="object-cover w-full h-full"
                                  />
                                </div>
                              </div>

                              <Box className="text-sm font-[inherit] select-none w-full truncate">
                                {style.name}
                              </Box>

                              <div className="flex items-center justify-center flex-shrink-0 w-0 h-0 m-0 transition-all duration-100 rounded-lg opacity-0 bg-gradient-to-r from-transparent group-hover:w-7 group-hover:h-7 group-hover:opacity-100 via-layer-200 to-layer-200 dark:via-layer-700 dark:to-layer-700">
                                <div className="flex items-center justify-center w-5 h-5 m-0 text-sm rounded shadow-button bg-layer-50 text-layer-600 hover:bg-layer-200 dark:bg-purple-500 dark:text-purple-50 dark:hover:bg-purple-600">
                                  <ForgeIcon weight="solid" />
                                </div>
                              </div>
                            </Link>
                          </MenuItem>
                        </div>
                      ))}
                  </>
                )}
              </ul>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}
